import React, { useState, forwardRef } from 'react';
import Input, { InputProps } from './Input';

interface PasswordInputProps extends Omit<InputProps, 'type' | 'rightIcon'> {
    showStrengthIndicator?: boolean;
}

const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
    ({ showStrengthIndicator = false, value, ...props }, ref) => {
        const [showPassword, setShowPassword] = useState(false);

        const getPasswordStrength = (password: string): { strength: number; label: string; color: string } => {
            if (!password) return { strength: 0, label: '', color: '' };

            let strength = 0;
            if (password.length >= 8) strength++;
            if (password.length >= 12) strength++;
            if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
            if (/\d/.test(password)) strength++;
            if (/[^a-zA-Z0-9]/.test(password)) strength++;

            const labels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
            const colors = ['#EF4444', '#F59E0B', '#F59E0B', '#10B981', '#059669'];

            return {
                strength: Math.min(strength, 5),
                label: labels[strength - 1] || '',
                color: colors[strength - 1] || ''
            };
        };

        const passwordStrength = showStrengthIndicator && value
            ? getPasswordStrength(value as string)
            : null;

        const toggleButton = (
            <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-[#015FA6] hover:text-[#014f8c] transition-colors p-1"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
            >
                {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path
                            d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"
                            stroke="currentColor"
                            strokeWidth={1.5}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <path d="M1 1l22 22" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path
                            d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
                            stroke="currentColor"
                            strokeWidth={1.5}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth={1.5} />
                    </svg>
                )}
            </button>
        );

        return (
            <div className="w-full">
                <Input
                    ref={ref}
                    type={showPassword ? 'text' : 'password'}
                    value={value}
                    rightIcon={toggleButton}
                    {...props}
                />
                {showStrengthIndicator && passwordStrength && passwordStrength.strength > 0 && (
                    <div className="mt-2">
                        <div className="flex gap-1 mb-1">
                            {[1, 2, 3, 4, 5].map((level) => (
                                <div
                                    key={level}
                                    className="h-1 flex-1 rounded-full transition-all duration-300"
                                    style={{
                                        backgroundColor: level <= passwordStrength.strength
                                            ? passwordStrength.color
                                            : '#E5E7EB'
                                    }}
                                />
                            ))}
                        </div>
                        <p
                            className="text-xs font-manrope"
                            style={{ color: passwordStrength.color }}
                        >
                            Password strength: {passwordStrength.label}
                        </p>
                    </div>
                )}
            </div>
        );
    }
);

PasswordInput.displayName = 'PasswordInput';

export default PasswordInput;
